import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useMemo, useState } from 'react'
import { format } from '../../../utils/date'
import Holidays from 'date-holidays'
import Tips from '../common/Tips'
import { uniqueId } from 'lodash'
import { useHistory } from 'react-router-dom'
import { setPath, setUrlId } from '../../../utils/url'
import { URL } from '../../constants/url'

interface AutoLabeling {
  id: number | null
  services: string[]
  keyword: {
    operator: string | null
    words: string[]
  }
  file_paths: string[]
  url: {
    pattern: string | null
    words: string[]
  }
  teams: {
    id: number
    name: string
  }[]
  members: {
    id: number
    name: string
  }[]
}

interface LabelGroup {
  id: number // ラベルグループID
  name: string // ラベルグループ名
  code: string | null
  color: string
  disabled: boolean // 非活性フラグ
  scheduled_start_date: string | null
  scheduled_end_date: string | null
  report_view: boolean
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: { // ラベル
    id: number // ラベルID
    label_master_id: number
    name: string // ラベル名
    code: string | null
    color: string // 色
    auto_labelings: AutoLabeling[]
    disabled: boolean // 非活性フラグ
  }[]
  auto_budgets: {
    start_date: string
    end_date: string | null
    budget: number
  }[]
  auto_other_costs: {
    start_date: string
    end_date: string | null
    name: string
    cost: number
  }[]
  auto_scheduled_times: {
    start_date: string
    end_date: string | null
    time: number
  }[]
  created_at: string
  created_by: string | null
}

interface Team {
  id: number
  name: string
  members: {
    id: number
    name: string
  }[]
  managers: {
    id: number
    name: string
  }[]
}

interface GroupedCost {
  start_date: string
  end_date: string | null
  list: {
    name: string
    value: string
  }[]
}

interface Props {
  list: LabelGroup
  deleteLabelGroupCallback: (labelGroupId: number) => void
  disableLabelGroupCallback: (labelGroupId: number, disable: boolean) => void
  copyLabelGroupCallback: (labelGroupId: number) => void
  teams: Team[]
  creatable?: boolean
}

const LabelGroupModule: FC<Props> = (props) => {
  const {
    list,
    deleteLabelGroupCallback,
    disableLabelGroupCallback,
    copyLabelGroupCallback,
    teams,
    creatable,
  } = props

  const history = useHistory()

  const holidays = new Holidays()
  holidays.init('JP')

  const [isDisplay, setIsDisplay] = useState<boolean>(false)

  const invalidProjectMessage = useMemo(() => {
    const tmpMsgList:string[] = []
    if (!list.labels.filter((v) => !v.disabled).length) {
      tmpMsgList.push("業務")
    }
    if (!list.permission.usable.members.length && !list.permission.usable.teams.length) {
      tmpMsgList.push("参加チーム・メンバー")
    }
    if (!list.permission.viewable.members.length && !list.permission.viewable.teams.length) {
      tmpMsgList.push("表示チーム・メンバー")
    }
    if (tmpMsgList.length) {
      return `${tmpMsgList.join("と")}が設定されていません！`
    }
    return ""
  }, [list.labels, list.permission])

  const groupedCosts = useMemo(() => {
    let tmp: GroupedCost[] = []
    list.auto_other_costs.forEach((v) => {
      const target = tmp.find((w) => w.start_date === v.start_date && w.end_date === v.end_date)
      if (target) {
        tmp = tmp.map((w) => {
          if (w.start_date === v.start_date && w.end_date === v.end_date) {
            return { ...w, list: [...w.list, { name: v.name || "", value: v.cost.toString() }] }
          }
          return w
        })
      } else {
        tmp.push({
          start_date: v.start_date,
          end_date: v.end_date,
          list: [
            {
              name: v.name || "",
              value: v.cost.toString(),
            },
          ],
        })
      }
    })
    return tmp
  }, [])

  const calendarColor = (day: string) => {
    const holiday = holidays.isHoliday(new Date(day))
    if ((holiday && holiday[0].type === "public") || format(day, 'd') === '0') {
      return "#FF0000"
    }
    if (format(day, 'd') === '6') {
      return "#317AB7"
    }
    return "#666666"
  }

  const dayOfWeek = (day: string | undefined) => {
    if (!day) return ""
    const holiday = holidays.isHoliday(new Date(day))
    let dd = format(day, "dd", "ja")
    if (holiday && holiday[0].type === "public") {
      dd += "・祝"
    }
    return (
      <>
        (
        <span style={{ color: calendarColor(day) }}>
          {dd}
        </span>
        )
      </>
    )
  }

  const clickCopy = (id: number) => {
    setIsDisplay(false)
    copyLabelGroupCallback(id)
  }

  const clickDetail = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_LABEL_SETTING_DETAIL.path, 'id', id), "ja")}`,
      state: { keep: true },
    })
  }

  const clickViewReport = (id: number) => {
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_DETAIL_DETAIL.path, 'id', id), "ja")}`,
    })
  }

  const autoLabelSettingCount = (autoLabelings?: AutoLabeling[]) => {
    if (!autoLabelings?.length) return 0
    return autoLabelings.length
  }

  return (
    <>
      <div className={`project_row ${isDisplay ? "open" : "close"}`}>
        <div
          className="project_header"
          onClick={() => setIsDisplay(!isDisplay)}
          role="none"
        >
          <div className="title" style={{ width: "100%" }}>
            <FontAwesomeIcon icon={['fas', isDisplay ? 'angle-down' : 'angle-right']} className="icon" fixedWidth />
            <div
              className="color"
              style={{
                backgroundColor: list.color,
              }}
            />
            <div className="text">
              {list.name}
              <div className="sub_area">
                <div className="sub_item">
                  <div className="item_label" style={{ backgroundColor: "#4b4c69" }}>
                    業務数
                  </div>
                  <div>
                    {`${list.labels.filter((v) => !v.disabled).length}件`}
                  </div>
                </div>
                {
                  list.code && (
                    <div className="sub_item">
                      <div className="item_label">
                        プロジェクトコード
                      </div>
                      <div>
                        {list.code}
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
            {
              list.disabled && (
                <div className="completed">
                  完了済
                </div>
              )
            }
            {
              invalidProjectMessage && (
                <Tips
                  description={invalidProjectMessage}
                  className=""
                  icon={false}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'exclamation-circle']}
                    style={{
                      color: "#FF0000",
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Tips>
              )
            }
          </div>
          <div className="detail">
            <div className="first">
              <div>
                <div className="title">
                  予定期間
                </div>
                <div>
                  {
                    list.scheduled_start_date ? (
                      <>
                        {format(list.scheduled_start_date, "YYYY/M/D")}
                        {dayOfWeek(list.scheduled_start_date)}
                      </>
                    ) : (
                      <>
                        未設定
                      </>
                    )
                  }
                  &nbsp;~&nbsp;
                  {
                    list.scheduled_end_date ? (
                      <>
                        {format(list.scheduled_end_date, "YYYY/M/D")}
                        {dayOfWeek(list.scheduled_end_date)}
                      </>
                    ) : (
                      <>
                        未設定
                      </>
                    )
                  }
                </div>
              </div>
              <div>
                <div className="title">
                  作成者
                </div>
                <div>
                  {list.created_by || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
        {
          isDisplay ? (
            <div className="project_detail">
              <div className="button_area">
                {
                  list.report_view && (
                    <Tips
                      description="プロジェクトのレポートを見る"
                      className=""
                      icon={false}
                    >
                      <button
                        type="button"
                        onClick={() => clickViewReport(list.id)}
                        className="view"
                      >
                        <FontAwesomeIcon icon={['fas', 'file-alt']} />
                        &nbsp;
                        レポートを見る
                      </button>
                    </Tips>
                  )
                }
                {
                  list.disabled ? (
                    <>
                      {
                        creatable && (
                          <>
                            <Tips
                              description="プロジェクトをコピーする"
                              className=""
                              icon={false}
                            >
                              <button
                                type="button"
                                onClick={() => clickCopy(list.id)}
                                className="copy"
                              >
                                <FontAwesomeIcon icon={['fas', 'copy']} />
                                &nbsp;
                                コピーする
                              </button>
                            </Tips>
                            <Tips
                              description="プロジェクトを未完了に戻す"
                              className=""
                              icon={false}
                            >
                              <button
                                type="button"
                                onClick={() => disableLabelGroupCallback(list.id, false)}
                              >
                                <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                                &nbsp;
                                未完了に戻す
                              </button>
                            </Tips>
                          </>
                        )
                      }
                    </>
                  ) : (
                    <>
                      {
                        creatable && (
                          <Tips
                            description="プロジェクトをコピーする"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              onClick={() => clickCopy(list.id)}
                              className="copy"
                            >
                              <FontAwesomeIcon icon={['fas', 'copy']} />
                              &nbsp;
                              コピーする
                            </button>
                          </Tips>
                        )
                      }
                      <Tips
                        description="プロジェクトを編集する"
                        className=""
                        icon={false}
                      >
                        <button
                          type="button"
                          onClick={() => clickDetail(list.id)}
                        >
                          <FontAwesomeIcon icon={['fas', 'tools']} />
                          &nbsp;
                          編集する
                        </button>
                      </Tips>
                      {
                        creatable && (
                          <Tips
                            description="プロジェクトを完了にする"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              className="complete"
                              onClick={() => disableLabelGroupCallback(list.id, true)}
                            >
                              <FontAwesomeIcon icon={['fas', 'check-circle']} />
                              &nbsp;
                              完了にする
                            </button>
                          </Tips>
                        )
                      }
                      {
                        creatable && (
                          <Tips
                            description="プロジェクトを削除する"
                            className=""
                            icon={false}
                          >
                            <button
                              type="button"
                              className="delete"
                              onClick={() => deleteLabelGroupCallback(list.id)}
                            >
                              <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                              &nbsp;
                              削除する
                            </button>
                          </Tips>
                        )
                      }
                    </>
                  )
                }
              </div>
              <div className="team_area">
                <div className="title">
                  プロジェクト表示チーム・メンバー
                </div>
                {
                  !!list.permission.viewable.category_managers?.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px",
                          borderBottom: "solid 1px #CCCCCC",
                          backgroundColor: "#EEEEEE",
                        }}
                      >
                        <div>
                          <Tips
                            description="プロジェクトが所属しているカテゴリーの表示メンバーです。"
                            className=""
                          >
                            <>
                              カテゴリー表示メンバー
                            </>
                          </Tips>
                        </div>
                      </div>
                      <div
                        className="details"
                        style={{
                          padding: "8px",
                        }}
                      >
                        {
                          list.permission.viewable.category_managers.map((member) => (
                            <div
                              className="detail"
                              key={`category_manager_${member.id}`}
                            >
                              {member.name}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  list.permission.viewable.teams.map((team) => (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px",
                          borderBottom: "solid 1px #CCCCCC",
                          backgroundColor: "#EEEEEE",
                        }}
                      >
                        <div>
                          {`チーム「${team.name}」のマネージャー`}
                        </div>
                      </div>
                      <div
                        className="details"
                        style={{
                          padding: "8px",
                        }}
                      >
                        {
                          teams.find((v) => v.id === team.id)?.managers.map((member) => (
                            <div
                              className="detail"
                              key={`manager_${member.id}`}
                            >
                              {member.name}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))
                }
                <div className="details">
                  {
                    list.permission.viewable.members.map((member) => (
                      <div
                        className="detail"
                        key={uniqueId()}
                      >
                        {member.name}
                      </div>
                    ))
                  }
                  {
                    !list.permission.viewable.teams.length && !list.permission.viewable.members.length && (
                      <>
                        プロジェクト表示チーム・メンバーがいません
                      </>
                    )
                  }
                </div>
              </div>
              <div className="team_area">
                <div className="title">
                  プロジェクト参加チーム・メンバー
                </div>
                {
                  list.permission.usable.teams.map((team) => (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px",
                          borderBottom: "solid 1px #CCCCCC",
                          backgroundColor: "#EEEEEE",
                        }}
                      >
                        <div>
                          {`チーム「${team.name}」のメンバー`}
                        </div>
                      </div>
                      <div
                        className="details"
                        style={{
                          padding: "8px",
                        }}
                      >
                        {
                          teams.find((v) => v.id === team.id)?.members.map((member) => (
                            <div
                              className="detail"
                              key={`member_${member.id}`}
                            >
                              {member.name}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))
                }
                <div className="details">
                  {
                    list.permission.usable.members.map((member) => (
                      <div
                        className="detail"
                        key={uniqueId()}
                      >
                        {member.name}
                      </div>
                    ))
                  }
                  {
                    !list.permission.usable.teams.length && !list.permission.usable.members.length && (
                      <>
                        プロジェクト参加チーム・メンバーがいません
                      </>
                    )
                  }
                </div>
              </div>
              {
                creatable && (
                  <>
                    <div className="table_list_wrap labeling_table_view">
                      <div className="table_list">
                        <table>
                          <thead>
                            <tr>
                              <th>
                                業務名
                              </th>
                              <th>
                                自動仕分け条件
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              !list.labels.length && (
                                <tr>
                                  <td colSpan={4}>
                                    業務が設定されていません
                                  </td>
                                </tr>
                              )
                            }
                            {
                              list.labels.filter((v) => !v.disabled).map((value) => (
                                <tr
                                  className={`${(list.disabled) ? "disabled" : ""}`}
                                  key={`label_${value.id}`}
                                >
                                  <td>
                                    <div>
                                      <div
                                        style={{
                                          backgroundColor: value.color,
                                        }}
                                      />
                                      {value.name}
                                    </div>
                                    {
                                      value.code && (
                                        <div className="code">
                                          {`(業務コード: ${value.code})`}
                                        </div>
                                      )
                                    }
                                  </td>
                                  <td>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexFlow: "column",
                                        gap: "8px",
                                      }}
                                    >
                                      {
                                        value.auto_labelings?.map((auto_labeling, j) => (
                                          <div
                                            key={`${value.id}_auto_labeling_${auto_labeling.id}`}
                                            style={{
                                              border: autoLabelSettingCount(value.auto_labelings) > 1 ? "solid 1px #CCCCCC" : "none",
                                              borderRadius: "4px",
                                              padding: "8px",
                                              textAlign: "left",
                                            }}
                                          >
                                            <div
                                              style={{
                                                color: "#666666",
                                                display: "flex",
                                                alignItems: "normal",
                                                flexFlow: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              {autoLabelSettingCount(value.auto_labelings) > 1 ? `条件${j + 1}` : ""}
                                              {
                                                auto_labeling.services.length ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        padding: "2px 4px",
                                                        backgroundColor: "#999999",
                                                        color: "#FFFFFF",
                                                        borderRadius: "4px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      アプリケーション
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.2em",
                                                      }}
                                                    >
                                                      {auto_labeling.services.join(',')}
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                auto_labeling.keyword.words.length ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        padding: "2px 4px",
                                                        backgroundColor: "#999999",
                                                        color: "#FFFFFF",
                                                        borderRadius: "4px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      キーワード
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.2em",
                                                      }}
                                                    >
                                                      {`【${auto_labeling.keyword.operator === "or" ? "いずれかを含む" : "全てを含む"}】`}
                                                      {auto_labeling.keyword.words.join(',')}
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                auto_labeling.teams.length || auto_labeling.members.length ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        padding: "2px 4px",
                                                        backgroundColor: "#999999",
                                                        color: "#FFFFFF",
                                                        borderRadius: "4px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      チーム・メンバー
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.2em",
                                                      }}
                                                    >
                                                      {
                                                        [
                                                          ...auto_labeling.teams.map((v) => v.name),
                                                          ...auto_labeling.members.map((v) => v.name),
                                                        ].join(',')
                                                      }
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                auto_labeling.url?.words.length ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        padding: "2px 4px",
                                                        backgroundColor: "#999999",
                                                        color: "#FFFFFF",
                                                        borderRadius: "4px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      URL
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.2em",
                                                      }}
                                                    >
                                                      {`【${auto_labeling.url.pattern === "exact" ? "完全一致" : "部分一致"}】`}
                                                      {auto_labeling.url.words.join(',')}
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                              {
                                                auto_labeling.file_paths.length ? (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "flex-start",
                                                      gap: "4px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        padding: "2px 4px",
                                                        backgroundColor: "#999999",
                                                        color: "#FFFFFF",
                                                        borderRadius: "4px",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      ファイルパス
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.2em",
                                                      }}
                                                    >
                                                      {auto_labeling.file_paths.join(',')}
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <></>
                                                )
                                              }
                                            </div>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {
                      !!list.auto_budgets.length && (
                        <div className="team_area">
                          <div className="title">
                            予算
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                            }}
                          >
                            {
                              list.auto_budgets.map((v) => (
                                <div
                                  key={`auto_budgets_${v.start_date}`}
                                  style={{
                                    borderBottom: "dashed 1px #CCCCCC",
                                    display: "flex",
                                    padding: "4px 8px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    color: "#999999",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                    }}
                                  >
                                    <div>
                                      {format(v.start_date, "YYYY年MM月")}
                                    </div>
                                    <div>
                                      ~
                                    </div>
                                    <div>
                                      {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                    </div>
                                  </div>
                                  <div>
                                    {`${v.budget.toLocaleString()}円`}
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                    {
                      !!groupedCosts.length && (
                        <div className="team_area">
                          <div className="title">
                            その他のコスト
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "16px",
                            }}
                          >
                            {
                              groupedCosts.map((v) => (
                                <div
                                  key={`auto_other_costs_${v.start_date}`}
                                  style={{
                                    position: "relative",
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "4px",
                                    display: "flex",
                                    flexFlow: "column",
                                    padding: "8px",
                                    gap: "4px",
                                    color: "#999999",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "-10px",
                                      left: "8px",
                                      display: "flex",
                                      gap: "4px",
                                      backgroundColor: "#FFFFFF",
                                      padding: "2px 8px",
                                    }}
                                  >
                                    <div>
                                      {format(v.start_date, "YYYY年MM月")}
                                    </div>
                                    <div>
                                      ~
                                    </div>
                                    <div>
                                      {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                    </div>
                                  </div>
                                  {
                                    v.list.map((w) => (
                                      <div
                                        key={`auto_other_costs_detail_${w.name}`}
                                        style={{
                                          borderBottom: "dashed 1px #CCCCCC",
                                          display: "flex",
                                          padding: "4px 8px",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          color: "#999999",
                                        }}
                                      >
                                        <div>
                                          {w.name || "項目名なし"}
                                        </div>
                                        <div>
                                          {`${Number(w.value || 0).toLocaleString()}円`}
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                    {
                      !!list.auto_scheduled_times.length && (
                        <div className="team_area">
                          <div className="title">
                            予定時間
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                            }}
                          >
                            {
                              list.auto_scheduled_times.map((v) => (
                                <div
                                  key={`auto_scheduled_times_${v.start_date}`}
                                  style={{
                                    borderBottom: "dashed 1px #CCCCCC",
                                    display: "flex",
                                    padding: "4px 8px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    color: "#999999",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                    }}
                                  >
                                    <div>
                                      {format(v.start_date, "YYYY年MM月")}
                                    </div>
                                    <div>
                                      ~
                                    </div>
                                    <div>
                                      {v.end_date ? format(v.end_date, "YYYY年MM月") : "指定なし"}
                                    </div>
                                  </div>
                                  <div>
                                    {`${Math.floor(v.time / 3600)}時間`}
                                    {
                                      v.time % 3600 ? (
                                        <>
                                          {`${(v.time % 3600) / 60}分`}
                                        </>
                                      ) : (
                                        <></>
                                      )
                                    }
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                  </>
                )
              }
            </div>
          ) : (
            <></>
          )
        }
      </div>
    </>
  )
}

LabelGroupModule.defaultProps = {
  creatable: true,
}

export default LabelGroupModule
